import { isLineKind, requiresSite, showsDuration, type LineKind } from '../domain/lineKind.js';
import {
  createSite,
  fetchAllJobs,
  fetchNotifications,
  fetchWeekSignoff,
  fetchWeekSignoffBanner,
  releaseWeek,
  unreleaseWeek,
  type WeekSignoffSummary,
} from '../api/client.js';
import {
  listClientsCached,
  listJobsInRange,
  listPendingConfirmationsLocal,
  listWorkTypesCached,
  listSitesCached,
  listRateOverridesCached,
  getJob,
  mergeJobsFromServer,
  putJobLocal,
  putSiteCached,
  touchClientUsed,
  touchSiteUsed,
  dateOnly,
  type CachedWorkType,
  type CachedSite,
  type CachedRateOverride,
  type DurationType,
  type LocalJob,
} from '../db/jobsDb.js';
import { splitProvisionalByAge } from '../../../src/domain/provisional.js';
import {
  listEmptyHtml,
  listErrorHtml,
  listSkeletonHtml,
} from './listStates.js';
import {
  bindJobRowActions,
  jobRowFromLocal,
  jobRowHtml,
  type JobRowView,
} from './jobRow.js';

export type CalendarHooks = {
  onBookDay: (isoDate: string) => void;
  onLogDay: (isoDate?: string) => void;
  onOpenJob: (id: string) => void;
  onEditJob: (id: string) => void;
  onChange: () => void;
};

function todayISO(): string {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Format YYYY-MM-DD for display; never emit "Invalid Date". */
function formatDayLabel(
  iso: string,
  opts: Intl.DateTimeFormatOptions,
): string {
  const day = dateOnly(iso);
  if (!day) return iso || 'Unknown date';
  const d = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString('en-GB', { ...opts, timeZone: 'UTC' });
}

/** ISO weekday: Mon=1 … Sun=7 */
function isoWeekday(iso: string): number {
  const js = new Date(`${iso}T12:00:00Z`).getUTCDay(); // Sun=0
  return js === 0 ? 7 : js;
}

export function recurringDates(
  from: string,
  weeks: number,
  weekdays: number[],
): string[] {
  if (weeks < 1 || weekdays.length === 0) return [];
  const want = new Set(weekdays);
  const end = addDays(from, weeks * 7 - 1);
  const out: string[] = [];
  let cur = from;
  while (cur <= end) {
    if (want.has(isoWeekday(cur))) out.push(cur);
    cur = addDays(cur, 1);
  }
  return out;
}

function monthMatrix(year: number, month0: number): (string | null)[][] {
  const first = new Date(Date.UTC(year, month0, 1));
  const startPad = (first.getUTCDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
  const cells: (string | null)[] = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = new Date(Date.UTC(year, month0, d)).toISOString().slice(0, 10);
    cells.push(iso);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  const rows: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
  return rows;
}

function statusClass(s: LocalJob['status']): string {
  if (s === 'planned') return 'st-planned';
  if (s === 'cancelled') return 'st-cancelled';
  return 'st-completed';
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function shortClient(name: string): string {
  const t = name.trim();
  if (t.length <= 10) return t;
  return `${t.slice(0, 9)}…`;
}

function kindOf(t: CachedWorkType | undefined): LineKind {
  return t && isLineKind(t.line_kind) ? t.line_kind : 'day_rate';
}

const WEEKDAY_LABELS: { n: number; label: string }[] = [
  { n: 1, label: 'Mon' },
  { n: 2, label: 'Tue' },
  { n: 3, label: 'Wed' },
  { n: 4, label: 'Thu' },
  { n: 5, label: 'Fri' },
  { n: 6, label: 'Sat' },
  { n: 7, label: 'Sun' },
];

function formatPence(p: number): string {
  return `£${(p / 100).toFixed(2)}`;
}

function formatPounds(p: number): string {
  return `£${Math.round(p / 100).toLocaleString('en-GB')}`;
}

function weekReleaseCardHtml(
  s: WeekSignoffSummary,
  releasing: boolean,
): string {
  const pending = s.clients.flatMap((c) =>
    c.jobs.filter((j) => !j.released_at),
  );
  const carry = pending.filter((j) => j.carried_forward);
  const blocked = s.clients.filter((c) => c.rate_blocked);
  const can = s.clients.some((c) => c.can_release);
  const weekLabel = `${formatDayLabel(s.week.start, {
    day: 'numeric',
    month: 'short',
  })} – ${formatDayLabel(s.week.end, {
    day: 'numeric',
    month: 'short',
  })}`;
  const lines = pending
    .map((j) => {
      const client =
        s.clients.find((c) => c.client_id === j.client_id)?.client_name ??
        'Client';
      const when = formatDayLabel(j.work_date, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
      const amt =
        j.amount_pence != null ? formatPence(j.amount_pence) : j.rate_error || '—';
      const cf = j.carried_forward ? ' · carried forward' : '';
      return `<li>${escapeHtml(client)} · ${escapeHtml(when)}${cf} · ${escapeHtml(amt)}</li>`;
    })
    .join('');
  const blocks = [
    ...new Set(
      blocked.flatMap((c) =>
        c.jobs.map((j) => j.rate_error).filter((m): m is string => Boolean(m)),
      ),
    ),
  ];

  let action: string;
  if (can) {
    action = `<button type="button" class="btn-primary cal-ready-invoice" data-release-week="${s.week.start}" ${releasing ? 'disabled' : ''}>
      ${releasing ? 'Releasing…' : `Release week · ${s.pending_count} job${s.pending_count === 1 ? '' : 's'} · ${formatPounds(s.pending_pence)}`}
    </button>`;
  } else if (blocked.length > 0 && pending.length > 0) {
    action = `<p class="cal-signoff-block">${escapeHtml(blocks.join(' '))}</p>`;
  } else if (s.released_count > 0 && s.pending_count === 0) {
    action = `<p class="muted">Released for Saturday’s invoice run · ${formatPence(s.total_pence)}</p>`;
  } else {
    action = `<p class="muted">Nothing releasable this week.</p>`;
  }

  return `<section class="cal-week-release today-card" aria-label="Week release">
    <h2>This week</h2>
    <p class="muted">${escapeHtml(weekLabel)}</p>
    ${
      pending.length
        ? `<p class="today-metric"><strong>${formatPence(s.pending_pence)}</strong>
            <span class="muted">${s.pending_count} job${s.pending_count === 1 ? '' : 's'} to release${
              carry.length
                ? ` · ${carry.length} carried forward`
                : ''
            }</span></p>
           <ul class="cal-week-release-list">${lines}</ul>
           ${
             carry.length
               ? '<p class="hint">Includes unreleased work from earlier weeks.</p>'
               : ''
           }`
        : ''
    }
    ${action}
    <button type="button" class="btn-secondary" data-cal-view="week" data-goto-signoff-week="${s.week.start}">Review week narratives</button>
  </section>`;
}

/**
 * Traditional month grid with bookings + recurring weekly booking creator.
 * Landing screen: agenda from today, unconfirmed banner, unbilled, quick actions.
 */
export async function mountCalendar(
  root: HTMLElement,
  hooks: CalendarHooks,
): Promise<{
  refresh: () => Promise<void>;
  focusUnconfirmed: () => void;
  focusWeek: (weekStart: string) => void;
}> {
  const today = todayISO();
  let cursor = new Date(`${today}T12:00:00Z`);
  let selectedDay: string | null = today;
  let calView: 'agenda' | 'month' | 'week' = 'month';
  /** Monday of the week shown in week / sign-off view. */
  let weekCursor = (() => {
    const wd = isoWeekday(today);
    return addDays(today, 1 - wd);
  })();
  let weekSignoff: WeekSignoffSummary | null = null;
  /** Always the ISO week containing today — used by the on-page release card. */
  let currentWeekSignoff: WeekSignoffSummary | null = null;
  let weekSignoffError = '';
  let weekReleasing = false;
  let showRecurring = false;
  const recurWeekdays = new Set<number>([1, 2, 3, 4, 5]);
  let recurWeeks = 8;
  let recurDuration: DurationType = 'full_day';
  let flash = '';
  /** When set, show site picker before confirming this planned job. */
  let sitePromptJobId: string | null = null;
  let sitePromptQuery = '';
  let sitePromptFlash = '';
  let refreshSeq = 0;
  let hasRendered = false;
  let dayPanelOpen = false;

  function kindOfJob(
    job: LocalJob,
    workTypes: CachedWorkType[],
  ): LineKind {
    const t = workTypes.find((w) => w.id === job.work_type_id);
    return t && isLineKind(t.line_kind) ? t.line_kind : 'day_rate';
  }

  function jobMissingSite(job: LocalJob, workTypes: CachedWorkType[]): boolean {
    if (!requiresSite(kindOfJob(job, workTypes))) return false;
    return (
      (job.site_ids?.length ?? 0) === 0 && !job.site_reference?.trim()
    );
  }

  async function completeJob(job: LocalJob) {
    const now = new Date().toISOString();
    await putJobLocal({
      ...job,
      status: 'completed',
      confirmed_at: now,
      syncState: 'pending',
      pendingOp: job.pendingOp === 'create' ? 'create' : 'update',
      updatedAt: now,
      failReason: null,
    });
    hooks.onChange();
  }

  /** Attach site (or site note) and complete exactly one planned job. */
  async function completeJobWithSite(
    job: LocalJob,
    opts: { siteIds?: string[]; siteReference?: string | null },
  ) {
    if (job.status !== 'planned') return;
    const siteIds = opts.siteIds ?? job.site_ids ?? [];
    const siteReference =
      opts.siteReference !== undefined ? opts.siteReference : job.site_reference;
    await completeJob({
      ...job,
      site_ids: siteIds,
      site_reference: siteReference,
    });
  }

  function confirmSiteListHtml(
    sites: CachedSite[],
    query: string,
  ): string {
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? sites.filter((s) => s.name.toLowerCase().includes(needle))
      : sites.slice(0, 12);
    const exact = sites.some((s) => s.name.toLowerCase() === needle);
    const trimmed = query.trim();
    return `${filtered
      .map(
        (s) =>
          `<button type="button" class="site-chip" data-confirm-pick-site="${s.id}">${escapeHtml(s.name)}</button>`,
      )
      .join('') || '<p class="meta">No saved sites — type a site name below.</p>'}
    ${
      needle && !exact
        ? `<button type="button" class="site-chip site-create" data-confirm-create-site="${escapeHtml(trimmed)}">Create site “${escapeHtml(trimmed)}”</button>`
        : ''
    }
    ${
      trimmed
        ? `<button type="button" class="submit cal-confirm-site-ref" data-confirm-site-ref="${escapeHtml(trimmed)}">Confirm this day only · “${escapeHtml(trimmed)}”</button>`
        : ''
    }`;
  }

  async function confirmJob(id: string, workTypes: CachedWorkType[]) {
    const job = await getJob(id);
    if (!job || job.status !== 'planned') return;
    if (jobMissingSite(job, workTypes)) {
      sitePromptJobId = id;
      sitePromptQuery = '';
      sitePromptFlash =
        'Pick or type a site for this day only — other weekly series days stay planned.';
      dayPanelOpen = false;
      await refresh();
      return;
    }
    sitePromptJobId = null;
    await completeJob(job);
  }

  async function refresh() {
    const seq = ++refreshSeq;
    if (selectedDay) {
      selectedDay = dateOnly(selectedDay) ?? selectedDay;
    }
    const y = cursor.getUTCFullYear();
    const m = cursor.getUTCMonth();
    const monthStart = new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
    const monthEnd = new Date(Date.UTC(y, m + 1, 0)).toISOString().slice(0, 10);
    // Agenda: look back a year and forward 18 months so historical work appears.
    const agendaFrom = addDays(today, -365);
    const agendaEnd = addDays(today, 548);

    if (!hasRendered) {
      root.innerHTML = `
        <div class="cal-toolbar">
          <div class="cal-title">Loading…</div>
        </div>
        ${listSkeletonHtml(3, 'list-skel-row skel-cal')}
      `;
    }

    // Pull the visible month (plus padding) so tapping days always has data.
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
      try {
        const pullFrom = addDays(monthStart, -7);
        const pullTo = addDays(monthEnd, 7);
        const remote = await fetchAllJobs({ from: pullFrom, to: pullTo });
        if (seq !== refreshSeq) return;
        await mergeJobsFromServer(remote);
      } catch (err) {
        console.warn('calendar month pull failed', err);
      }
    }
    if (seq !== refreshSeq) return;

    let listError = false;
    let monthJobs: LocalJob[] = [];
    let agendaJobs: LocalJob[] = [];
    let unconfirmed: LocalJob[] = [];
    let clients: Awaited<ReturnType<typeof listClientsCached>> = [];
    let workTypes: Awaited<ReturnType<typeof listWorkTypesCached>> = [];
    let sites: CachedSite[] = [];
    let overrides: CachedRateOverride[] = [];
    let unbilled = {
      count: 0,
      amount_pence: 0,
      oldest_work_date: null as string | null,
      day_span: 0,
    };
    let signoffBanner: {
      show_banner: boolean;
      pending_count: number;
      pending_pence?: number;
      pending_days?: number;
      week: { start: string; end: string };
    } | null = null;
    weekSignoff = null;
    currentWeekSignoff = null;
    weekSignoffError = '';
    try {
      [monthJobs, agendaJobs, unconfirmed, clients, workTypes, sites, overrides] =
        await Promise.all([
          listJobsInRange(monthStart, monthEnd),
          listJobsInRange(agendaFrom, agendaEnd),
          listPendingConfirmationsLocal(today),
          listClientsCached(),
          listWorkTypesCached(),
          listSitesCached(),
          listRateOverridesCached(),
        ]);
      try {
        const remote = await fetchNotifications();
        unbilled = remote.unbilled;
      } catch (err) {
        console.warn('notifications fetch failed', err);
      }
      try {
        signoffBanner = await fetchWeekSignoffBanner();
      } catch (err) {
        console.warn('week sign-off banner failed', err);
      }
      const currentWeekStart = addDays(today, 1 - isoWeekday(today));
      try {
        currentWeekSignoff = await fetchWeekSignoff(currentWeekStart);
      } catch (err) {
        console.warn('current week sign-off fetch failed', err);
      }
      if (calView === 'week') {
        try {
          weekSignoff =
            weekCursor === currentWeekStart && currentWeekSignoff
              ? currentWeekSignoff
              : await fetchWeekSignoff(weekCursor);
        } catch (err) {
          console.warn('week sign-off fetch failed', err);
          weekSignoffError =
            err instanceof Error ? err.message : 'Could not load week sign-off';
        }
      }
    } catch (err) {
      console.error('calendar refresh failed', err);
      listError = true;
    }
    if (seq !== refreshSeq) return;
    if (listError) {
      root.innerHTML = `
        <div class="cal-toolbar">
          <button type="button" class="cal-nav" data-nav="-1" aria-label="Previous month">‹</button>
          <div class="cal-title">Calendar</div>
          <button type="button" class="cal-nav" data-nav="1" aria-label="Next month">›</button>
        </div>
        ${listErrorHtml()}
      `;
      root.querySelectorAll('[data-nav]').forEach((el) => {
        el.addEventListener('click', () => {
          const delta = Number((el as HTMLElement).dataset.nav);
          cursor = new Date(Date.UTC(y, m + delta, 1));
          void refresh();
        });
      });
      root.querySelector('[data-list-retry]')?.addEventListener('click', () => {
        void refresh();
      });
      return;
    }
    const clientName = new Map(clients.map((c) => [c.id, c.name]));
    const byDate = new Map<string, LocalJob[]>();
    for (const j of monthJobs) {
      if (j.status === 'cancelled') continue;
      const day = dateOnly(j.work_date) ?? j.work_date;
      const list = byDate.get(day) ?? [];
      list.push(j);
      byDate.set(day, list);
    }

    const agendaByDate = new Map<string, LocalJob[]>();
    for (const j of agendaJobs) {
      if (j.status === 'cancelled') continue;
      const day = dateOnly(j.work_date) ?? j.work_date;
      const list = agendaByDate.get(day) ?? [];
      list.push(j);
      agendaByDate.set(day, list);
    }

    const monthLabel = cursor.toLocaleString('en-GB', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });

    let sitePromptJob =
      sitePromptJobId
        ? monthJobs.find((j) => j.id === sitePromptJobId) ??
          agendaJobs.find((j) => j.id === sitePromptJobId) ??
          unconfirmed.find((j) => j.id === sitePromptJobId) ??
          null
        : null;
    if (sitePromptJobId && !sitePromptJob) {
      sitePromptJob = (await getJob(sitePromptJobId)) ?? null;
    }

    let sitePromptHtml = '';
    if (sitePromptJob) {
      const sites = await listSitesCached(sitePromptJob.client_id);
      const clientLabel =
        clientName.get(sitePromptJob.client_id) ?? 'Client';
      const dayOnlyLabel = formatDayLabel(sitePromptJob.work_date, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
      sitePromptHtml = `<section class="cal-site-prompt today-card" id="cal-site-prompt">
        <h2 class="money-subhead">Confirm one day</h2>
        <p class="muted">${escapeHtml(clientLabel)} · ${escapeHtml(dayOnlyLabel)} only — rest of series unchanged</p>
        ${sitePromptFlash ? `<p class="flash error">${escapeHtml(sitePromptFlash)}</p>` : ''}
        <label class="field">
          <span>Site for this day</span>
          <input type="search" id="confirm-site-search" value="${escapeHtml(sitePromptQuery)}" placeholder="Type site name, then confirm" autocomplete="off" />
        </label>
        <div class="site-multi" id="confirm-site-list">
          ${confirmSiteListHtml(sites, sitePromptQuery)}
        </div>
        <button type="button" class="btn-secondary" data-cancel-site-prompt>Cancel</button>
      </section>`;
    }

    const { recent: recentUnconfirmed, stale: staleUnconfirmed } =
      splitProvisionalByAge(unconfirmed, today);
    const unconfirmedBanner =
      unconfirmed.length > 0
        ? `<button type="button" class="cal-banner cal-banner-unconfirmed${
            staleUnconfirmed.length > 0 ? ' is-stale' : ''
          }" data-focus-unconfirmed>
            <strong>${unconfirmed.length} unconfirmed job${unconfirmed.length === 1 ? '' : 's'}</strong>
            <span>Bookings and logged days still provisional — they will not be invoiced until you confirm.</span>
            ${
              staleUnconfirmed.length > 0
                ? `<span class="cal-banner-urgent">${staleUnconfirmed.length} more than 14 days old</span>`
                : '<span class="muted">Tap to confirm each day</span>'
            }
          </button>`
        : '';

    const releaseBanner =
      signoffBanner?.show_banner
        ? `<button type="button" class="cal-banner cal-banner-release" data-cal-view="week" data-goto-signoff-week="${signoffBanner.week.start}">
            <strong>${signoffBanner.pending_days ?? signoffBanner.pending_count} day${(signoffBanner.pending_days ?? signoffBanner.pending_count) === 1 ? '' : 's'} · ${formatPounds(signoffBanner.pending_pence ?? 0)} not yet released</strong>
            <span class="muted">Thursday–Saturday — review the week and release before the invoice run</span>
          </button>`
        : '';

    const unbilledCard =
      unbilled.count > 0 || unbilled.amount_pence > 0
        ? `<section class="today-card today-unbilled" aria-label="Unbilled work">
            <h2>Unbilled</h2>
            <p class="today-metric">
              <strong>${formatPence(unbilled.amount_pence)}</strong>
              <span class="muted">${unbilled.count} day${unbilled.count === 1 ? '' : 's'}${
                unbilled.day_span > 0
                  ? ` · spanning ${unbilled.day_span} calendar day${unbilled.day_span === 1 ? '' : 's'}`
                  : ''
              }</span>
            </p>
          </section>`
        : '';

    const weekReleaseCard = currentWeekSignoff
      ? weekReleaseCardHtml(currentWeekSignoff, weekReleasing)
      : '';

    const quickActions = `<div class="cal-quick-actions">
      <button type="button" class="btn-primary" data-log-day>Log a day</button>
    </div>`;

    const weeks = monthMatrix(y, m);
    const monthHtml = `
      <div class="cal-month">
        <div class="cal-weekdays">
          ${WEEKDAY_LABELS.map((d) => `<span>${d.label}</span>`).join('')}
        </div>
        ${weeks
          .map(
            (row) => `<div class="cal-week">
              ${row
                .map((iso) => {
                  if (!iso) return `<div class="cal-day empty" aria-hidden="true"></div>`;
                  const jobs = byDate.get(iso) ?? [];
                  const isToday = iso === today;
                  const isSel = iso === selectedDay;
                  const chips = jobs
                    .slice(0, 3)
                    .map((j) => {
                      const name = shortClient(
                        clientName.get(j.client_id) ?? 'Job',
                      );
                      const mark =
                        j.status === 'planned'
                          ? 'P·'
                          : j.status === 'cancelled'
                            ? '×·'
                            : '✓·';
                      return `<span class="cal-chip ${statusClass(j.status)}" title="${escapeHtml(clientName.get(j.client_id) ?? '')} (${j.status})">${mark}${escapeHtml(name)}</span>`;
                    })
                    .join('');
                  const more =
                    jobs.length > 3
                      ? `<span class="cal-chip more">+${jobs.length - 3}</span>`
                      : '';
                  return `<button type="button" class="cal-day ${isToday ? 'today' : ''} ${isSel ? 'selected' : ''} ${jobs.length ? 'has-jobs' : ''}" data-day="${iso}" aria-label="${iso}, ${jobs.length} booking${jobs.length === 1 ? '' : 's'}">
                    <span class="cal-num">${Number(iso.slice(8, 10))}</span>
                    <span class="cal-chips">${chips}${more}</span>
                  </button>`;
                })
                .join('')}
            </div>`,
          )
          .join('')}
      </div>`;

    const dayJobs: LocalJob[] = [];
    if (selectedDay) {
      const seen = new Set<string>();
      for (const j of [...monthJobs, ...agendaJobs, ...unconfirmed]) {
        const day = dateOnly(j.work_date) ?? j.work_date;
        if (day !== selectedDay || seen.has(j.id)) continue;
        seen.add(j.id);
        dayJobs.push(j);
      }
    }

    const dayLabel = selectedDay
      ? formatDayLabel(selectedDay, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })
      : '';

    const jobLookups = { clients, workTypes, sites, overrides };
    const dayRows = dayJobs.map((j) =>
      jobRowFromLocal(j, jobLookups),
    );
    const dayOverlay =
      dayPanelOpen && selectedDay
        ? `<div class="cal-day-overlay" role="dialog" aria-modal="true" aria-label="${escapeHtml(dayLabel)}">
            <button type="button" class="cal-day-overlay-backdrop" data-close-day-panel aria-label="Close day"></button>
            <div class="cal-day-overlay-sheet">
              <div class="cal-day-panel-head">
                <strong>${escapeHtml(dayLabel)}</strong>
                <button type="button" class="btn-ghost" data-close-day-panel>Close</button>
              </div>
              <div class="cal-day-panel-actions">
                <button type="button" class="btn-book" data-book="${selectedDay}">Add booking</button>
                <button type="button" class="btn-book secondary" data-log-on="${selectedDay}">Add job</button>
                ${
                  dayJobs.some((j) => j.status === 'planned' && !j.invoice_run_id)
                    ? `<button type="button" class="btn-confirm-day" data-confirm-day="${selectedDay}">Confirm day</button>`
                    : ''
                }
              </div>
              ${
                dayJobs.length === 0
                  ? `<p class="hint">Nothing booked or logged on this day.</p>`
                  : dayRows.map((row) => jobRowHtml(row)).join('')
              }
            </div>
          </div>`
        : '';

    const dayRateTypes = workTypes.filter((t) => kindOf(t) === 'day_rate');
    const typeOptions = (dayRateTypes.length ? dayRateTypes : workTypes)
      .map(
        (t) =>
          `<option value="${t.id}">${escapeHtml(t.short_description)}</option>`,
      )
      .join('');

    const recurPreview = recurringDates(
      selectedDay ?? today,
      recurWeeks,
      [...recurWeekdays],
    );

    const recurringPanel = showRecurring
      ? `<div class="cal-recur" role="dialog" aria-label="Recurring booking">
          <div class="cal-recur-head">
            <h2>Weekly series</h2>
            <button type="button" class="btn-ghost" data-close-recur>Close</button>
          </div>
          <p class="hint">Creates planned bookings on the chosen weekdays for the next ${recurWeeks} week${recurWeeks === 1 ? '' : 's'}. Confirm each day when worked.</p>
          <label class="field">
            <span>Client</span>
            <select id="recur-client" required>
              ${
                clients.length
                  ? clients
                      .map(
                        (c) =>
                          `<option value="${c.id}">${escapeHtml(c.name)}</option>`,
                      )
                      .join('')
                  : '<option value="">No clients cached — sign in when online</option>'
              }
            </select>
          </label>
          <label class="field">
            <span>Work type</span>
            <select id="recur-work-type" required>
              ${typeOptions || '<option value="">No work types</option>'}
            </select>
          </label>
          <label class="field">
            <span>Starts</span>
            <input type="date" id="recur-from" value="${escapeHtml(selectedDay ?? today)}" />
          </label>
          <label class="field">
            <span>Repeat for</span>
            <select id="recur-weeks">
              ${[4, 6, 8, 12, 16, 26]
                .map(
                  (w) =>
                    `<option value="${w}" ${w === recurWeeks ? 'selected' : ''}>${w} weeks</option>`,
                )
                .join('')}
            </select>
          </label>
          <fieldset class="field">
            <legend>On these days</legend>
            <div class="cal-dow" role="group" aria-label="Weekdays">
              ${WEEKDAY_LABELS.map(
                (d) => `<button type="button" class="dow-chip ${recurWeekdays.has(d.n) ? 'on' : ''}" data-dow="${d.n}" aria-pressed="${recurWeekdays.has(d.n)}">${d.label}</button>`,
              ).join('')}
            </div>
          </fieldset>
          <label class="field" id="recur-duration-field">
            <span>Duration</span>
            <div class="duration" role="group">
              <button type="button" data-recur-dur="full_day" aria-pressed="${recurDuration === 'full_day'}">Full day</button>
              <button type="button" data-recur-dur="half_day" aria-pressed="${recurDuration === 'half_day'}">Half day</button>
            </div>
          </label>
          <label class="field">
            <span>Site (optional)</span>
            <input type="text" id="recur-site" placeholder="e.g. Main yard — confirm each day separately" maxlength="500" autocomplete="off" />
          </label>
          <p class="cal-recur-preview"><strong>${recurPreview.length}</strong> booking${recurPreview.length === 1 ? '' : 's'} will be created</p>
          ${flash ? `<p class="flash ${flash.startsWith('Created') ? '' : 'error'}">${escapeHtml(flash)}</p>` : ''}
          <button type="button" class="submit" id="recur-submit" ${recurPreview.length === 0 || !clients.length ? 'disabled' : ''}>Create series</button>
        </div>`
      : '';

    const agendaDates = [...agendaByDate.keys()].sort();
    const agendaHtml =
      agendaDates.length === 0
        ? listEmptyHtml('No bookings from today onward.', {
            label: 'Book day',
            dataAttr: `data-book="${selectedDay ?? today}"`,
          })
        : `<div class="cal-agenda">
            ${agendaDates
              .map((iso) => {
                const jobs = agendaByDate.get(iso) ?? [];
                const label = formatDayLabel(iso, {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                  });
                return `<section class="cal-agenda-day ${iso === selectedDay ? 'is-selected' : ''}" ${iso === unconfirmed[0]?.work_date ? 'data-unconfirmed-anchor' : ''}>
                  <button type="button" class="cal-agenda-head" data-day="${iso}">
                    <strong>${escapeHtml(label)}</strong>
                    <span class="muted">${jobs.length}</span>
                  </button>
                  <ul>
                    ${jobs
                      .map(
                        (j) => `<li class="${statusClass(j.status)}">
                          <button type="button" class="cal-agenda-job" data-day="${iso}">
                            <span>${escapeHtml(clientName.get(j.client_id) ?? 'Client')}${j.invoice_run_id ? ' · invoiced' : ''}</span>
                            <span class="muted">${j.status === 'planned' ? 'provisional' : j.status.replace('_', ' ')}</span>
                          </button>
                        </li>`,
                      )
                      .join('')}
                  </ul>
                </section>`;
              })
              .join('')}
          </div>`;

    function unconfirmedRow(j: LocalJob, urgent: boolean): string {
      const label = formatDayLabel(j.work_date, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
      return `<li class="${urgent ? 'is-stale' : ''}">
        <div>
          <strong>${escapeHtml(label)}</strong>
          <span class="muted"> · ${escapeHtml(clientName.get(j.client_id) ?? 'Client')}</span>
          ${urgent ? '<span class="cal-stale-tag">Over 14 days</span>' : ''}
        </div>
        <button type="button" class="btn-secondary btn-compact" data-confirm="${j.id}">Confirm</button>
      </li>`;
    }

    const unconfirmedList =
      unconfirmed.length > 0 && calView === 'agenda'
        ? `<section class="cal-unconfirmed-list" id="cal-unconfirmed">
            ${
              staleUnconfirmed.length > 0
                ? `<h2 class="money-subhead cal-unconfirmed-urgent">Still provisional — over 14 days</h2>
            <ul class="today-list">
              ${staleUnconfirmed.map((j) => unconfirmedRow(j, true)).join('')}
            </ul>`
                : ''
            }
            ${
              recentUnconfirmed.length > 0
                ? `<h2 class="money-subhead">${staleUnconfirmed.length > 0 ? 'Unconfirmed this fortnight' : 'Unconfirmed'}</h2>
            <ul class="today-list">
              ${recentUnconfirmed.map((j) => unconfirmedRow(j, false)).join('')}
            </ul>`
                : ''
            }
            ${
              unconfirmed.length > 1
                ? `<p class="hint">Confirm each day separately — never the whole weekly series at once.</p>`
                : ''
            }
          </section>`
        : '';

    const weekEnd = addDays(weekCursor, 6);
    const weekTitle = `${formatDayLabel(weekCursor, {
      day: 'numeric',
      month: 'short',
    })} – ${formatDayLabel(weekEnd, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })}`;

    const toolbarTitle =
      calView === 'week'
        ? weekTitle
        : calView === 'month'
          ? monthLabel
          : 'Agenda';
    const toolbarNavLabel =
      calView === 'week' ? 'week' : calView === 'month' ? 'month' : 'period';

    let weekHtml = '';
    if (calView === 'week') {
      if (weekSignoffError && !weekSignoff) {
        weekHtml = `<div class="cal-week-signoff">${listErrorHtml(weekSignoffError)}</div>`;
      } else if (!weekSignoff) {
        weekHtml = `<div class="cal-week-signoff">${listSkeletonHtml(3, 'list-skel-row skel-cal')}</div>`;
      } else {
        const s = weekSignoff;
        const clientBlocks =
          s.clients.length === 0
            ? listEmptyHtml('No confirmed billable work for this week.')
            : s.clients
                .map((g) => {
                  const jobs = g.jobs
                    .map((j) => {
                      const extras = [
                        j.carried_forward ? 'Prior week' : null,
                        j.line_kind === 'expense' ? 'Expense' : null,
                        j.rate_error
                          ? /account code/i.test(j.rate_error)
                            ? 'No account code'
                            : 'No rate'
                          : null,
                      ].filter((p): p is string => Boolean(p));
                      const row: JobRowView = {
                        id: j.id,
                        clientName: formatDayLabel(j.work_date, {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                        }),
                        siteLabel: j.site_label,
                        workTypeLabel: '',
                        durationLabel: j.duration_label,
                        rateLabel:
                          j.amount_pence != null
                            ? formatPence(j.amount_pence)
                            : j.rate_error,
                        narrative: j.narrative,
                        statusKind: 'completed',
                        statusLabel: j.released_at ? 'Released' : 'Confirmed',
                        invoiced: false,
                        invoiceLabel: null,
                        extraPills: extras,
                        actions: j.released_at ? ['unrelease'] : ['edit'],
                      };
                      return jobRowHtml(row);
                    })
                    .join('');
                  return `<section class="cal-signoff-client${g.rate_blocked ? ' is-blocked' : ''}">
                    <div class="cal-signoff-client-head">
                      <strong>${escapeHtml(g.client_name)}</strong>
                      <span>${formatPence(g.total_pence)}</span>
                    </div>
                    ${
                      g.rate_blocked
                        ? `<p class="cal-signoff-block">${escapeHtml(
                            [
                              ...new Set(
                                g.jobs
                                  .map((j) => j.rate_error)
                                  .filter((m): m is string => Boolean(m)),
                              ),
                            ].join(' ') ||
                              'No resolvable rate — fix this client before releasing their week.',
                          )}</p>`
                        : ''
                    }
                    ${jobs}
                    ${
                      g.can_release
                        ? `<button type="button" class="btn-secondary" data-release-client="${g.client_id}" data-week-start="${s.week.start}">Release ${escapeHtml(g.client_name)}</button>`
                        : ''
                    }
                  </section>`;
                })
                .join('');
        const provisionalBlock =
          (s.provisional ?? []).length > 0
            ? `<section class="cal-signoff-attention">
                <h2 class="money-subhead">Still provisional</h2>
                <p class="hint">Confirm these before you treat the week as ready. They will not be invoiced.</p>
                ${(s.provisional ?? [])
                  .map((j) =>
                    jobRowHtml({
                      id: j.id,
                      clientName: j.client_name,
                      siteLabel: j.site_label,
                      workTypeLabel: '',
                      durationLabel: j.duration_label,
                      rateLabel: null,
                      narrative: j.narrative,
                      statusKind: 'planned',
                      statusLabel: 'Provisional',
                      invoiced: false,
                      invoiceLabel: null,
                      dateLabel: formatDayLabel(j.work_date, {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                      }),
                      actions: ['confirm', 'edit'],
                    }),
                  )
                  .join('')}
              </section>`
            : '';
        const nonBillableBlock =
          (s.non_billable ?? []).length > 0
            ? `<section class="cal-signoff-attention">
                <h2 class="money-subhead">Non-billable (excluded)</h2>
                ${(s.non_billable ?? [])
                  .map((j) =>
                    jobRowHtml({
                      id: j.id,
                      clientName: j.client_name,
                      siteLabel: j.site_label,
                      workTypeLabel: '',
                      durationLabel: j.duration_label,
                      rateLabel: null,
                      narrative: j.narrative,
                      statusKind: 'completed',
                      statusLabel: 'Non-billable',
                      invoiced: false,
                      invoiceLabel: null,
                      dateLabel: formatDayLabel(j.work_date, {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                      }),
                      actions: ['edit'],
                    }),
                  )
                  .join('')}
              </section>`
            : '';
        const releasable = s.clients.some((g) => g.can_release);
        const cta = releasable
          ? `<button type="button" class="btn-primary cal-ready-invoice" data-release-week="${s.week.start}" ${weekReleasing ? 'disabled' : ''}>
                ${weekReleasing ? 'Releasing…' : `Release week · ${s.pending_count} job${s.pending_count === 1 ? '' : 's'} · ${formatPounds(s.pending_pence)}`}
              </button>`
          : s.released_count > 0 && s.pending_count === 0
            ? `<p class="cal-signoff-ready muted">Released for Saturday’s invoice run · ${formatPence(s.total_pence)}</p>`
            : '';
        weekHtml = `<div class="cal-week-signoff">
          ${weekSignoffError ? listErrorHtml(weekSignoffError) : ''}
          ${flash ? `<p class="flash">${escapeHtml(flash)}</p>` : ''}
          <p class="hint">Gate 2 — Friday review. Confirming a day is Gate 1; this release says the week is ready to invoice.${s.clients.some((c) => c.jobs.some((j) => j.carried_forward)) ? ' Earlier unreleased WIP is included.' : ''}</p>
          ${
            s.release_warning
              ? `<p class="cal-banner cal-banner-inline">${escapeHtml(s.release_warning)}</p>`
              : ''
          }
          ${
            s.saturday_approaching && s.pending_count > 0
              ? `<p class="cal-banner cal-banner-release cal-banner-inline"><strong>${s.pending_days} day${s.pending_days === 1 ? '' : 's'} · ${formatPounds(s.pending_pence)} not yet released</strong></p>`
              : ''
          }
          <div class="cal-week-signoff-clients">${clientBlocks}</div>
          ${s.clients.length > 0 ? `<p class="cal-signoff-total"><strong>Week total</strong> ${formatPence(s.total_pence)}</p>` : ''}
          ${provisionalBlock}
          ${nonBillableBlock}
          ${cta}
        </div>`;
      }
    }

    const mainCal =
      calView === 'week'
        ? weekHtml
        : calView === 'month'
          ? monthHtml
          : agendaHtml;

    root.innerHTML = `
      ${unconfirmedBanner}
      <div class="cal-toolbar">
        <button type="button" class="cal-nav" data-nav="-1" aria-label="Previous ${toolbarNavLabel}">‹</button>
        <div class="cal-title">${escapeHtml(toolbarTitle)}</div>
        <button type="button" class="cal-nav" data-nav="1" aria-label="Next ${toolbarNavLabel}">›</button>
      </div>
      ${releaseBanner}
      ${unbilledCard}
      ${weekReleaseCard}
      ${quickActions}
      ${sitePromptHtml}
      <div class="cal-view-toggle" role="group" aria-label="Calendar view">
        <button type="button" data-cal-view="agenda" aria-pressed="${calView === 'agenda'}">Agenda</button>
        <button type="button" data-cal-view="week" aria-pressed="${calView === 'week'}">Week</button>
        <button type="button" data-cal-view="month" aria-pressed="${calView === 'month'}">Month</button>
      </div>
      ${unconfirmedList}
      ${mainCal}
      ${recurringPanel}
      ${dayOverlay}
    `;
    hasRendered = true;

    root.querySelector('[data-log-day]')?.addEventListener('click', () => {
      hooks.onLogDay();
    });

    const confirmSiteSearch = root.querySelector(
      '#confirm-site-search',
    ) as HTMLInputElement | null;

    async function refreshConfirmSiteList() {
      const list = root.querySelector('#confirm-site-list');
      if (!list || !sitePromptJobId) return;
      const job = await getJob(sitePromptJobId);
      if (!job) return;
      const sites = await listSitesCached(job.client_id);
      list.innerHTML = confirmSiteListHtml(sites, sitePromptQuery);
      bindConfirmSiteListActions();
    }

    function bindConfirmSiteListActions() {
      root.querySelectorAll('[data-confirm-pick-site]').forEach((el) => {
        el.addEventListener('click', () => {
          void (async () => {
            if (!sitePromptJobId) return;
            const job = await getJob(sitePromptJobId);
            if (!job || job.status !== 'planned') return;
            const siteId = (el as HTMLElement).dataset.confirmPickSite!;
            void touchSiteUsed(siteId);
            sitePromptJobId = null;
            sitePromptFlash = '';
            await completeJobWithSite(job, {
              siteIds: [siteId],
              siteReference: null,
            });
            await refresh();
          })();
        });
      });
      root
        .querySelector('[data-confirm-create-site]')
        ?.addEventListener('click', () => {
          void (async () => {
            if (!sitePromptJobId) return;
            const name = (
              root.querySelector('[data-confirm-create-site]') as HTMLElement
            ).dataset.confirmCreateSite!.trim();
            const job = await getJob(sitePromptJobId);
            if (!job || !name) return;
            try {
              const created = await createSite({
                name,
                client_id: job.client_id,
              });
              const cached: CachedSite = {
                id: created.id,
                name: created.name,
                client_id: created.client_id,
                useCount: 0,
                lastUsedAt: new Date().toISOString(),
              };
              await putSiteCached(cached);
              sitePromptJobId = null;
              sitePromptFlash = '';
              await completeJobWithSite(job, {
                siteIds: [created.id],
                siteReference: null,
              });
              await refresh();
            } catch (err) {
              // Offline / locked: still confirm this one day with a site note.
              sitePromptJobId = null;
              sitePromptFlash = '';
              await completeJobWithSite(job, {
                siteIds: [],
                siteReference: name,
              });
              flash =
                err instanceof Error
                  ? `Confirmed this day with site note (could not create site: ${err.message})`
                  : 'Confirmed this day with site note';
              await refresh();
            }
          })();
        });
      root
        .querySelector('[data-confirm-site-ref]')
        ?.addEventListener('click', () => {
          void (async () => {
            if (!sitePromptJobId) return;
            const ref = (
              root.querySelector('[data-confirm-site-ref]') as HTMLElement
            ).dataset.confirmSiteRef!.trim();
            const job = await getJob(sitePromptJobId);
            if (!job || !ref || job.status !== 'planned') return;
            sitePromptJobId = null;
            sitePromptFlash = '';
            await completeJobWithSite(job, {
              siteIds: [],
              siteReference: ref,
            });
            await refresh();
          })();
        });
    }

    confirmSiteSearch?.addEventListener('input', () => {
      sitePromptQuery = confirmSiteSearch.value;
      void refreshConfirmSiteList();
    });
    confirmSiteSearch?.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        const refBtn = root.querySelector(
          '[data-confirm-site-ref]',
        ) as HTMLButtonElement | null;
        refBtn?.click();
      }
    });
    root.querySelector('[data-cancel-site-prompt]')?.addEventListener('click', () => {
      sitePromptJobId = null;
      sitePromptFlash = '';
      void refresh();
    });
    bindConfirmSiteListActions();
    if (sitePromptJobId) {
      root
        .querySelector('#cal-site-prompt')
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      confirmSiteSearch?.focus();
    }
    function focusUnconfirmed() {
      calView = 'agenda';
      const first = staleUnconfirmed[0] ?? unconfirmed[0];
      if (first) selectedDay = first.work_date;
      void refresh().then(() => {
        root
          .querySelector('#cal-unconfirmed')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    root
      .querySelector('[data-focus-unconfirmed]')
      ?.addEventListener('click', () => {
        focusUnconfirmed();
      });

    root.querySelectorAll('[data-cal-view]').forEach((el) => {
      el.addEventListener('click', () => {
        const next = (el as HTMLElement).dataset.calView as
          | 'agenda'
          | 'month'
          | 'week';
        calView = next;
        const goto = (el as HTMLElement).dataset.gotoSignoffWeek;
        if (goto) weekCursor = goto;
        void refresh();
      });
    });
    root.querySelectorAll('[data-nav]').forEach((el) => {
      el.addEventListener('click', () => {
        const delta = Number((el as HTMLElement).dataset.nav);
        if (calView === 'week') {
          weekCursor = addDays(weekCursor, delta * 7);
        } else if (calView === 'month') {
          cursor = new Date(Date.UTC(y, m + delta, 1));
        } else {
          // Agenda: jump selected day by a week for quick browsing
          selectedDay = addDays(selectedDay ?? today, delta * 7);
        }
        void refresh();
      });
    });
    async function runRelease(weekStart: string, clientId?: string) {
      if (weekReleasing) return;
      const summaryFor =
        weekSignoff?.week.start === weekStart
          ? weekSignoff
          : currentWeekSignoff?.week.start === weekStart
            ? currentWeekSignoff
            : null;
      if (summaryFor?.release_warning) {
        const ok = window.confirm(
          `${summaryFor.release_warning}\n\nRelease confirmed jobs anyway?`,
        );
        if (!ok) return;
      }
      weekReleasing = true;
      weekSignoffError = '';
      flash = '';
      void refresh();
      try {
        const result = await releaseWeek(weekStart, { clientId });
        weekSignoff = result.summary;
        if (result.skipped_blocked > 0) {
          flash = `${result.skipped_blocked} job${result.skipped_blocked === 1 ? '' : 's'} not released — no resolvable rate`;
        }
        hooks.onChange();
      } catch (err) {
        flash =
          err instanceof Error
            ? err.message
            : 'Could not release — unlock online first.';
      } finally {
        weekReleasing = false;
        void refresh();
      }
    }

    root.querySelectorAll('[data-release-week]').forEach((el) => {
      el.addEventListener('click', () => {
        const weekStart = (el as HTMLElement).dataset.releaseWeek;
        if (weekStart) void runRelease(weekStart);
      });
    });
    root.querySelectorAll('[data-release-client]').forEach((el) => {
      el.addEventListener('click', () => {
        const btn = el as HTMLElement;
        const weekStart = btn.dataset.weekStart;
        const clientId = btn.dataset.releaseClient;
        if (weekStart && clientId) void runRelease(weekStart, clientId);
      });
    });
    root.querySelectorAll('[data-day]').forEach((el) => {
      el.addEventListener('click', () => {
        const raw = (el as HTMLElement).dataset.day ?? null;
        selectedDay = raw ? dateOnly(raw) ?? raw : null;
        dayPanelOpen = Boolean(selectedDay);
        flash = '';
        void refresh();
      });
    });
    root.querySelectorAll('[data-close-day-panel]').forEach((el) => {
      el.addEventListener('click', () => {
        dayPanelOpen = false;
        void refresh();
      });
    });
    root.querySelectorAll('[data-log-on]').forEach((el) => {
      el.addEventListener('click', () => {
        hooks.onLogDay((el as HTMLElement).dataset.logOn);
      });
    });
    root.querySelectorAll('[data-book]').forEach((el) => {
      el.addEventListener('click', () => {
        const iso = (el as HTMLElement).dataset.book!;
        hooks.onBookDay(iso);
      });
    });
    bindJobRowActions(root, {
      onConfirm: (id) => {
        void confirmId(id);
      },
      onEdit: (id) => {
        hooks.onEditJob(id);
      },
      onDelete: (id) => {
        void (async () => {
          const job = await getJob(id);
          if (!job || job.invoice_run_id) return;
          const ok = window.confirm(
            'Delete this job? It stays on this phone until sync removes it from the server.',
          );
          if (!ok) return;
          const now = new Date().toISOString();
          await putJobLocal({
            ...job,
            deleted_at: now,
            pendingOp: 'delete',
            syncState: 'pending',
            failReason: null,
            updatedAt: now,
          });
          hooks.onChange();
          await refresh();
        })();
      },
      onUnrelease: (id) => {
        void (async () => {
          const weekStart = weekSignoff?.week.start ?? weekCursor;
          try {
            const result = await unreleaseWeek(weekStart, { jobId: id });
            weekSignoff = result.summary;
            hooks.onChange();
          } catch (err) {
            flash =
              err instanceof Error
                ? err.message
                : 'Cannot un-release — Saturday’s run has already picked this up.';
          }
          await refresh();
        })();
      },
    });
    root.querySelector('[data-open-recur]')?.addEventListener('click', () => {
      showRecurring = true;
      flash = '';
      void refresh();
    });
    root.querySelector('[data-close-recur]')?.addEventListener('click', () => {
      showRecurring = false;
      flash = '';
      void refresh();
    });

    root.querySelectorAll('[data-dow]').forEach((el) => {
      el.addEventListener('click', () => {
        const n = Number((el as HTMLElement).dataset.dow);
        if (recurWeekdays.has(n)) recurWeekdays.delete(n);
        else recurWeekdays.add(n);
        flash = '';
        void refresh();
      });
    });
    root.querySelectorAll('[data-recur-dur]').forEach((el) => {
      el.addEventListener('click', () => {
        recurDuration = (el as HTMLElement).dataset.recurDur as DurationType;
        void refresh();
      });
    });
    const weeksSelect = root.querySelector('#recur-weeks') as HTMLSelectElement | null;
    weeksSelect?.addEventListener('change', () => {
      recurWeeks = Number(weeksSelect.value) || 8;
      void refresh();
    });
    const fromInput = root.querySelector('#recur-from') as HTMLInputElement | null;
    fromInput?.addEventListener('change', () => {
      if (fromInput.value) selectedDay = fromInput.value;
      void refresh();
    });

    root.querySelector('#recur-submit')?.addEventListener('click', () => {
      void (async () => {
        const clientSelect = root.querySelector(
          '#recur-client',
        ) as HTMLSelectElement;
        const typeSelect = root.querySelector(
          '#recur-work-type',
        ) as HTMLSelectElement;
        const fromEl = root.querySelector('#recur-from') as HTMLInputElement;
        const weeksEl = root.querySelector('#recur-weeks') as HTMLSelectElement;
        const siteEl = root.querySelector('#recur-site') as HTMLInputElement;
        const clientId = clientSelect?.value;
        const workTypeId = typeSelect?.value;
        const from = fromEl?.value || today;
        const weeks = Number(weeksEl?.value) || recurWeeks;
        const siteRef = siteEl?.value.trim() || null;
        if (!clientId || !workTypeId) {
          flash = 'Choose client and work type';
          void refresh();
          return;
        }
        const dates = recurringDates(from, weeks, [...recurWeekdays]);
        if (dates.length === 0) {
          flash = 'Pick at least one weekday';
          void refresh();
          return;
        }
        if (dates.length > 62) {
          flash = 'Too many dates (max 62) — shorten the series';
          void refresh();
          return;
        }
        const wt = workTypes.find((t) => t.id === workTypeId);
        const duration: DurationType = showsDuration(kindOf(wt))
          ? recurDuration
          : 'custom';
        const now = new Date().toISOString();
        for (const work_date of dates) {
          const job: LocalJob = {
            id: crypto.randomUUID(),
            work_date,
            client_id: clientId,
            work_type_id: workTypeId,
            duration_type: duration,
            custom_hours: null,
            custom_amount: null,
            site_ids: [],
            site_reference: siteRef,
            billable: true,
            status: 'planned',
            start_time: null,
            confirmed_at: null,
            released_at: null,
            xero_invoice_id: null,
            po_number: null,
            notes: null,
            syncState: 'pending',
            failReason: null,
            invoice_run_id: null,
            invoice_number: null,
            deleted_at: null,
            pendingOp: 'create',
            createdAt: now,
            updatedAt: now,
            lastSyncAttemptAt: null,
          };
          await putJobLocal(job);
        }
        await touchClientUsed(clientId);
        flash = `Created ${dates.length} planned booking${dates.length === 1 ? '' : 's'}`;
        showRecurring = false;
        hooks.onChange();
        await refresh();
      })();
    });

    async function confirmId(id: string): Promise<'done' | 'needs_site'> {
      await confirmJob(id, workTypes);
      if (sitePromptJobId) return 'needs_site';
      await refresh();
      return 'done';
    }

    root.querySelectorAll('[data-confirm]').forEach((el) => {
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        void confirmId((el as HTMLElement).dataset.confirm!);
      });
    });
    root.querySelectorAll('[data-confirm-day]').forEach((el) => {
      el.addEventListener('click', async () => {
        const day = (el as HTMLElement).dataset.confirmDay!;
        const jobs = (byDate.get(day) ?? []).filter((j) => j.status === 'planned');
        for (const j of jobs) {
          const result = await confirmId(j.id);
          // Stop so we only ask for a site for the first job that needs one;
          // never walk the rest of a weekly series.
          if (result === 'needs_site') return;
        }
      });
    });
    root.querySelectorAll('[data-cancel]').forEach((el) => {
      el.addEventListener('click', async (ev) => {
        ev.stopPropagation();
        const id = (el as HTMLElement).dataset.cancel!;
        const job = monthJobs.find((j) => j.id === id);
        if (!job || job.status !== 'planned' || job.invoice_run_id) return;
        const now = new Date().toISOString();
        await putJobLocal({
          ...job,
          status: 'cancelled',
          syncState: 'pending',
          pendingOp: job.pendingOp === 'create' ? 'create' : 'update',
          updatedAt: now,
          failReason: null,
        });
        hooks.onChange();
        await refresh();
      });
    });

    root.querySelectorAll('[data-open-job]').forEach((el) => {
      el.addEventListener('click', (ev) => {
        // Confirm/cancel buttons stopPropagation separately.
        if ((ev.target as HTMLElement).closest('[data-confirm],[data-cancel]')) {
          return;
        }
        hooks.onOpenJob((el as HTMLElement).dataset.openJob!);
      });
      el.addEventListener('keydown', (ev) => {
        const ke = ev as KeyboardEvent;
        if (ke.key !== 'Enter' && ke.key !== ' ') return;
        ke.preventDefault();
        hooks.onOpenJob((el as HTMLElement).dataset.openJob!);
      });
    });
  }

  function focusUnconfirmedFromOutside() {
    calView = 'agenda';
    void refresh().then(() => {
      root
        .querySelector('#cal-unconfirmed')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function focusWeek(weekStart: string) {
    calView = 'week';
    weekCursor = weekStart;
    void refresh();
  }

  document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape' || !dayPanelOpen) return;
    dayPanelOpen = false;
    void refresh();
  });

  await refresh();
  return {
    refresh,
    focusUnconfirmed: focusUnconfirmedFromOutside,
    focusWeek,
  };
}
